// Shared Settings chrome: the visual primitives (Section + control classes) and
// the sub-screen layout (back arrow → /settings + title) used by every grouped
// settings sub-screen. Kept in one place so the aggregator and its children
// stay visually identical and back-navigation is consistent.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/index.js';
import { useIsDesktop } from '../ui/AppShell.js';
import { SettingsDialog, type SettingsSection } from './SettingsDialog.js';

export const inputClass =
  'min-h-touch rounded-xl border border-edge bg-raised px-3 text-sm text-ink';
export const buttonClass =
  'min-h-touch rounded-xl bg-accent px-4 text-sm font-medium text-ink disabled:opacity-40 active:bg-accent-press';
export const quietButtonClass = 'min-h-touch rounded-xl bg-raised px-4 text-sm text-ink';

export function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section aria-label={title} className="flex flex-col gap-3 rounded-xl bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      {children}
    </section>
  );
}

/**
 * Layout for a settings sub-screen. Below the `desktop` breakpoint this is the
 * unchanged compact page: a back arrow returning to /settings, the group title,
 * then the group's sections (a pushed screen, unlike the Settings tab itself).
 * At `desktop`+ the same body renders inside the overlay {@link SettingsDialog}
 * (Telegram-style section nav + card) — the sub-screen's forms and handlers are
 * untouched; only the surrounding chrome differs. `section` highlights this
 * screen's entry in the dialog nav.
 */
export function SettingsSubScreen({
  section,
  title,
  children,
}: {
  section: SettingsSection;
  title: string;
  children: ReactNode;
}): ReactNode {
  const t = useT();
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <SettingsDialog activeSection={section} title={title}>
        {children}
      </SettingsDialog>
    );
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-4 p-6">
      <header className="flex items-center gap-3">
        <Link
          to="/settings"
          aria-label={t('chrome.backToSettings')}
          className="flex min-h-touch min-w-touch items-center justify-center rounded-xl bg-raised"
        >
          ←
        </Link>
        <h1 className="text-xl font-semibold">{title}</h1>
      </header>
      {children}
    </main>
  );
}
